import { Search } from "lucide-react";
import { RefObject, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ResponsiveModal } from "@/shared/ui/responsive-modal";

import type { SearchDialogProps } from "./domain/types";
import { SearchDialogContent } from "./ui/search-dialog-content";
import { SearchDialogFooter } from "./ui/search-dialog-footer";
import { SearchDialogTabs } from "./ui/search-dialog-tabs";
import { SearchTriggerButton } from "./ui/search-trigger-button";
import { useSearch } from "./use-search";

function SearchHeader({
  inputRef,
  query,
  onQueryChange,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  query: string;
  onQueryChange: (value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <ResponsiveModal.Header className="px-4 py-3 border-b shrink-0">
      <ResponsiveModal.Title className="sr-only">
        {t("search.title")}
      </ResponsiveModal.Title>
      <ResponsiveModal.Description className="sr-only">
        {t("search.description")}
      </ResponsiveModal.Description>
      <div className="relative flex items-center">
        <div className="absolute left-3 flex h-8 w-8 shrink-0 items-center justify-center pointer-events-none">
          <Search className="h-4 w-4 text-muted-foreground" />
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder={t("search.placeholder")}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          className="w-full h-auto py-1.5 pl-12 pr-10 text-base shadow-none focus-visible:ring-0 placeholder:text-muted-foreground bg-transparent border-none outline-none"
        />
      </div>
    </ResponsiveModal.Header>
  );
}

export function SearchDialog({
  open,
  onOpenChange,
  showTrigger = false,
  scrollOffset = 0,
  onScrollOffsetChange,
}: SearchDialogProps) {
  const search = useSearch(open, onOpenChange, 20);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen && scrollContainerRef.current && onScrollOffsetChange) {
        onScrollOffsetChange(scrollContainerRef.current.scrollTop);
      }
      onOpenChange(isOpen);
    },
    [onOpenChange, onScrollOffsetChange],
  );

  const trigger = showTrigger ? (
    <ResponsiveModal.Trigger asChild>
      <SearchTriggerButton
        isMac={search.isMac}
        onOpen={() => onOpenChange(true)}
      />
    </ResponsiveModal.Trigger>
  ) : undefined;

  return (
    <ResponsiveModal open={open} onOpenChange={handleOpenChange}>
      {trigger}
      <ResponsiveModal.Content className="p-0 gap-0 min-w-[400px]">
        <SearchHeader
          inputRef={search.inputRef}
          query={search.query}
          onQueryChange={search.setQuery}
        />
        <ResponsiveModal.Body className="p-0 flex flex-col">
          <SearchDialogTabs
            activeTab={search.activeTab}
            onTabChange={search.setActiveTab}
          />
          <div
            ref={scrollContainerRef}
            className="flex-1 min-h-0 overflow-y-auto"
          >
            <SearchDialogContent
              query={search.query}
              debouncedQuery={search.debouncedQuery}
              isPending={search.isPending}
              results={search.results}
              isFetchingNextPage={search.isFetchingNextPage}
              selectedIndex={search.selectedIndex}
              cursorRef={search.cursorRef}
              onSelect={search.handleSelect}
              scrollContainerRef={scrollContainerRef}
              savedScrollOffset={scrollOffset}
            />
          </div>
        </ResponsiveModal.Body>
        <ResponsiveModal.Footer className="p-4 pt-3 border-t shrink-0 hidden md:flex">
          <SearchDialogFooter isMac={search.isMac} />
        </ResponsiveModal.Footer>
      </ResponsiveModal.Content>
    </ResponsiveModal>
  );
}
